import { BoundingSphere, Cartesian2, Cartesian3, Ray, IntersectionTests, } from "cesium";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { PathSlopeType, } from "./interface";
import { NEAR_ZERO_THRESHOLD } from "../../core/utils";
// Width compensation constants for corner angles
const MIN_SIN_HALF_ANGLE_THRESHOLD = 0.001; // ~0.11° minimum angle
const MAX_WIDTH_MULTIPLIER = 1000; // Cap for extremely sharp angles
/**
 * Safely divides two numbers, returning a default value when the denominator is zero.
 * @param num - The numerator
 * @param denom - The denominator
 * @param result - The default value to return when denominator is zero (default: 0)
 * @returns The division result or the default value
 */
const divOr = (num, denom, result = 0) => Math.abs(denom) <= NEAR_ZERO_THRESHOLD ? result : num / denom;
export class PathGeometry {
    _id;
    _pathGeometry;
    _settings;
    _pathInfos;
    get id() {
        return this._id;
    }
    get geometry() {
        return this._pathGeometry;
    }
    get pathInfos() {
        return this._pathInfos;
    }
    constructor(id) {
        this._id = id;
        this._pathGeometry = { indices: [], vertices: [] };
        this._pathInfos = [];
    }
    dispose() {
        this._pathInfos.length = 0;
        this._settings = null;
    }
    /** Resampled points, each tagged with the source segment `points[segmentIndex] → points[segmentIndex + 1]`. */
    adjustPointsAsPerInterpolationPitch(points, interpolationPitch, hadToHavePoints) {
        const interpolatedPoints = [];
        //interpolation will not affect points closest to controlpoints, slopebreaks
        interpolatedPoints.push({ position: points[0], segmentIndex: 0 });
        for (let i = 1; i < points.length; ++i) {
            const currRawPathPoint = points[i];
            let lastInterPolatedPoint = interpolatedPoints[interpolatedPoints.length - 1].position;
            const dir = EarthBrainUtils.getNormalizedDirection(points[i - 1], currRawPathPoint);
            const dirz0 = EarthBrainUtils.getNormalizedDirection(new Cartesian3(points[i - 1].x, points[i - 1].y, 0), new Cartesian3(currRawPathPoint.x, currRawPathPoint.y, 0));
            const angle = Cartesian3.angleBetween(dir, dirz0);
            const interpolationPitchInHorizontalDir = interpolationPitch / Math.cos(angle);
            let distance3d = Cartesian3.distance(lastInterPolatedPoint, currRawPathPoint);
            const distance2d = Cartesian2.distance(lastInterPolatedPoint, currRawPathPoint);
            if (hadToHavePoints.findIndex((p) => Cartesian2.equals(p, currRawPathPoint)) !== -1 &&
                distance3d < 2 * interpolationPitchInHorizontalDir) {
                if (distance2d <= 0) {
                    interpolatedPoints.pop(); // use latest point if 2d distance is 0
                }
                interpolatedPoints.push({
                    position: points[i],
                    segmentIndex: i - 1,
                });
                continue;
            }
            if (distance2d <= 0) {
                continue;
            }
            while (distance3d > interpolationPitchInHorizontalDir) {
                // interpolation happening
                const sphere = new BoundingSphere(lastInterPolatedPoint, interpolationPitchInHorizontalDir);
                const ray = new Ray(points[i - 1], dir);
                const res = IntersectionTests.raySphere(ray, sphere);
                if (res && res.stop > 0) {
                    const point = EarthBrainUtils.getDistantCartesian(points[i - 1], dir, res.stop);
                    interpolatedPoints.push({ position: point, segmentIndex: i - 1 });
                }
                else {
                    break;
                }
                lastInterPolatedPoint =
                    interpolatedPoints[interpolatedPoints.length - 1].position;
                if (Cartesian3.distance(lastInterPolatedPoint, currRawPathPoint) >
                    distance3d) {
                    console.log("Something went wrong");
                }
                distance3d = Cartesian3.distance(lastInterPolatedPoint, currRawPathPoint);
            }
            if (hadToHavePoints.findIndex((p) => Cartesian2.equals(p, currRawPathPoint)) !== -1) {
                interpolatedPoints.pop();
                interpolatedPoints.push({
                    position: points[i],
                    segmentIndex: i - 1,
                });
            }
        }
        const lastGap = Cartesian3.distance(interpolatedPoints[interpolatedPoints.length - 1].position, points[points.length - 1]);
        if (lastGap > 0 && lastGap < interpolationPitch) {
            interpolatedPoints.pop();
            interpolatedPoints.push({
                position: points[points.length - 1],
                segmentIndex: points.length - 2,
            });
        }
        return interpolatedPoints;
    }
    initializePathPointInfos(points, mustHavePoints, interpolationPitch, getDirections) {
        const interpolatedPoints = this.adjustPointsAsPerInterpolationPitch(points, interpolationPitch, mustHavePoints);
        this._pathInfos.length = 0;
        for (let i = 0; i < interpolatedPoints.length; ++i) {
            const pathInfo = {
                position: interpolatedPoints[i].position,
                next: undefined,
                prev: this._pathInfos[i - 1],
                left: [],
                right: [],
                direction: undefined,
            };
            this._pathInfos.push(pathInfo);
            if (this._pathInfos[i - 1]) {
                this._pathInfos[i - 1].next = pathInfo;
            }
        }
        // Directions come from the alignment's own dense point list, not from these coarser samples
        const directions = getDirections(interpolatedPoints.map((p) => p.position), interpolatedPoints.map((p) => p.segmentIndex));
        this._pathInfos.forEach((pathInfo, i) => {
            pathInfo.direction = directions[i];
        });
    }
    getSidePoints(refPoint, direction) {
        const settings = this._settings.commonSetting;
        let finalLeftSlopeHeight;
        let finalRightSlopeHeight;
        // Note: slope setting stores inverse-slope ratio (1/n format) from UI
        // UI converts 0% gradient input to slope=0 (not infinity) to represent flat surface
        // Zero-division guard prevents Cesium normalization errors when calculating width/slope
        if (settings.pathSlopeType === PathSlopeType.STRAIGHT) {
            finalLeftSlopeHeight = divOr(settings.leftWidth, settings.slope);
            finalRightSlopeHeight = divOr(settings.rightWidth, -settings.slope);
        }
        else {
            finalLeftSlopeHeight = divOr(settings.leftWidth, settings.leftSlope);
            finalRightSlopeHeight = divOr(settings.rightWidth, settings.rightSlope);
        }
        const leftPoint = this.getEdgePointOfSide(refPoint, direction.left, direction.down, settings.leftWidth, finalLeftSlopeHeight);
        const rightPoint = this.getEdgePointOfSide(refPoint, direction.right, direction.down, settings.rightWidth, finalRightSlopeHeight);
        return {
            left: leftPoint,
            right: rightPoint,
        };
    }
    update(centralLinePoints, mustHavePoints, pathSetting, getDirections) {
        this.dispose();
        if (centralLinePoints.length < 2) {
            return;
        }
        this._settings = pathSetting;
        this.initializePathPointInfos(centralLinePoints, mustHavePoints, pathSetting.commonSetting.interpolationPitch, getDirections);
        this.computeSidePoints();
        this.buildPathGeometry();
    }
    //---------------------private methods----------------------------
    computeSidePoints() {
        const settings = this._settings.commonSetting;
        let finalLeftSlopeHeight;
        let finalRightSlopeHeight;
        // Note: slope setting stores inverse-slope ratio (1/n format) from UI
        // UI converts 0% gradient input to slope=0 (not infinity) to represent flat surface
        // Zero-division guard prevents Cesium normalization errors when calculating width/slope
        if (settings.pathSlopeType === PathSlopeType.STRAIGHT) {
            finalLeftSlopeHeight = divOr(settings.leftWidth, settings.slope);
            finalRightSlopeHeight = divOr(settings.rightWidth, -settings.slope);
        }
        else {
            finalLeftSlopeHeight = divOr(settings.leftWidth, settings.leftSlope);
            finalRightSlopeHeight = divOr(settings.rightWidth, settings.rightSlope);
        }
        // small out of (angle between incoming and left or right) and (180 -angle between incoming and left or right)  will be the half angle
        //Width multiplier = Width / sin(angle)
        let previousInfo = undefined;
        for (const info of this._pathInfos) {
            const widthMultiplier = this.calculateWidthMultiplier(info);
            const leftPoint = this.getEdgePointOfSide(info.position, info.direction.left, info.direction.down, settings.leftWidth * widthMultiplier, finalLeftSlopeHeight);
            info.left.push(leftPoint);
            const rightPoint = this.getEdgePointOfSide(info.position, info.direction.right, info.direction.down, settings.rightWidth * widthMultiplier, finalRightSlopeHeight);
            info.right.push(rightPoint);
            previousInfo = info;
        }
    }
    /**
     * Calculate width multiplier for corner compensation based on road corner angle
     * Uses the formula: 1 / sin(θ/2) to ensure road width is preserved at corners
     * Examples:
     * - 180° (straight): 1.0 (no compensation)
     * - 90° (right angle): 1.414 (√2)
     * - 60° (sharp): 2.0
     * @param roadCornerAngle The corner angle in degrees (0-180)
     * @returns Width multiplier (>= 1.0)
     */
    calculateWidthMultiplier(info) {
        if (!info.prev) {
            return 1;
        }
        // For corners, calculate geometrically accurate width compensation
        // based on the angle using the formula: 1 / sin(θ/2)
        // This ensures the road width is preserved at the corner
        const incomingDir = EarthBrainUtils.getNormalizedDirection(info.prev.position, info.position);
        const angle = Cartesian2.angleBetween(incomingDir, info.direction.left);
        const halfAngle = Math.min(Math.PI - angle, angle);
        // Avoid division by zero for very small angles
        if (halfAngle > MIN_SIN_HALF_ANGLE_THRESHOLD) {
            return 1 / Math.sin(halfAngle);
        }
        // For extremely sharp angles, cap at a reasonable maximum
        return MAX_WIDTH_MULTIPLIER;
    }
    getEdgePointOfSide(refPoint, sideDir, verticalDir, sideWidth, slopeHeight) {
        const pathVerticalDir = Cartesian3.multiplyByScalar(verticalDir, slopeHeight, new Cartesian3());
        const pathWidthDir = Cartesian3.multiplyByScalar(sideDir, sideWidth, new Cartesian3());
        const pathSlopeDir = Cartesian3.add(pathVerticalDir, pathWidthDir, new Cartesian3());
        const pathPoint = Cartesian3.add(refPoint, pathSlopeDir, new Cartesian3());
        return pathPoint;
    }
    /**
     * geometry of surface in clockwise orientation
     */
    buildPathGeometry() {
        const vertexSoup = [];
        const indexSoup = [];
        let currentInfo = this._pathInfos[0];
        while (currentInfo) {
            // Safety check: skip if left or right arrays are missing or empty
            if (!currentInfo.left ||
                currentInfo.left.length === 0 ||
                !currentInfo.right ||
                currentInfo.right.length === 0) {
                currentInfo = currentInfo.next;
                continue;
            }
            //left
            for (let i = 0; i < currentInfo.left.length - 1; ++i) {
                vertexSoup.push(currentInfo.position.x, currentInfo.position.y, currentInfo.position.z, currentInfo.left[i].x, currentInfo.left[i].y, currentInfo.left[i].z, currentInfo.left[i + 1].x, currentInfo.left[i + 1].y, currentInfo.left[i + 1].z);
                const index = indexSoup.length;
                indexSoup.push(index, index + 1, index + 2);
            }
            const next = currentInfo.next;
            if (next?.left?.length > 0 && currentInfo.left?.length > 0) {
                const lastLeft = currentInfo.left.at(-1);
                const firstNextLeft = next.left[0];
                if (lastLeft && firstNextLeft) {
                    vertexSoup.push(currentInfo.position.x, currentInfo.position.y, currentInfo.position.z, lastLeft.x, lastLeft.y, lastLeft.z, next.position.x, next.position.y, next.position.z);
                    let index = indexSoup.length;
                    indexSoup.push(index, index + 1, index + 2);
                    vertexSoup.push(next.position.x, next.position.y, next.position.z, lastLeft.x, lastLeft.y, lastLeft.z, firstNextLeft.x, firstNextLeft.y, firstNextLeft.z);
                    index = indexSoup.length;
                    indexSoup.push(index, index + 1, index + 2);
                }
            }
            //right
            for (let i = 0; i < currentInfo.right.length - 1; ++i) {
                vertexSoup.push(currentInfo.position.x, currentInfo.position.y, currentInfo.position.z, currentInfo.right[i + 1].x, currentInfo.right[i + 1].y, currentInfo.right[i + 1].z, currentInfo.right[i].x, currentInfo.right[i].y, currentInfo.right[i].z);
                const index = indexSoup.length;
                indexSoup.push(index, index + 1, index + 2);
            }
            if (next?.right?.length > 0 && currentInfo.right?.length > 0) {
                const lastRight = currentInfo.right.at(-1);
                const firstNextRight = next.right[0];
                if (lastRight && firstNextRight) {
                    vertexSoup.push(currentInfo.position.x, currentInfo.position.y, currentInfo.position.z, next.position.x, next.position.y, next.position.z, lastRight.x, lastRight.y, lastRight.z);
                    let index = indexSoup.length;
                    indexSoup.push(index, index + 1, index + 2);
                    vertexSoup.push(next.position.x, next.position.y, next.position.z, firstNextRight.x, firstNextRight.y, firstNextRight.z, lastRight.x, lastRight.y, lastRight.z);
                    index = indexSoup.length;
                    indexSoup.push(index, index + 1, index + 2);
                }
            }
            currentInfo = currentInfo.next;
        }
        this._pathGeometry.indices = indexSoup;
        this._pathGeometry.vertices = vertexSoup;
    }
}
//# sourceMappingURL=mid-path-geometry.js.map